import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IComplianceTableConfig } from '@/types/contracts';
import type { IContractRequirement, IContractRequirementItem } from '@/store/types';
import type { IGigComplianceRow } from '@/components/agent/gig-compliance/types';

import {
  CardContainer,
  HeaderLabel,
  Row,
  RowLabelCell,
  RowsContainer,
  RowsLabelsContainer,
} from '@/components/agent/gig-compliance/ComplianceTable.styles';
import { PathParams, RequirementType, UserType } from '@/store/enums';
import { media } from '@/styles/theme';
import { useAppDispatch, useAppSelector, usePathParam } from '@/hooks';
import {
  selectClientContractSchedules,
  selectContractSchedules,
  selectIsPendingAction,
} from '@/store/selectors/contractSelectors';
import { selectAuthMenuType } from '@/store/selectors/authSelectors';
import { LoaderWrapper, RequirementCell, RequirementsCardSkeleton } from '@/components/common';
import { getClientContractDetails } from '@/store/slices/contractSlice';

interface IProps {
  tableConfig: IComplianceTableConfig;
  requirements: IContractRequirement | undefined;
  rowsToDisplay: IGigComplianceRow[];
  isLoading: boolean;
}

const requirementsCardSelector = createSelector(
  selectAuthMenuType,
  selectContractSchedules,
  selectClientContractSchedules,
  selectIsPendingAction(getClientContractDetails.typePrefix),
  (userType, spContractSchedules, clientContractSchedules, isGetClientContractDetailsPending) => ({
    contractSchedules: userType === UserType.agent ? spContractSchedules : clientContractSchedules,
    isGetClientContractDetailsPending,
  }),
);

const RequirementsCard = ({ tableConfig, requirements, rowsToDisplay, isLoading }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { contractSchedules, isGetClientContractDetailsPending } = useAppSelector(requirementsCardSelector);
  const dispatch = useAppDispatch();
  const [spId] = usePathParam(PathParams.spId);
  const [contractId] = usePathParam(PathParams.spContractId);

  useEffect(() => {
    if (!spId || !contractId) return;
    dispatch(getClientContractDetails({ contractId, userId: spId }));
  }, [contractId, dispatch, spId]);

  const minMaxHeaders = useCallback(
    (type: RequirementType.max | RequirementType.min) => {
      const text =
        type === RequirementType.min
          ? t`gigCompliance.complianceTable.reqMin`
          : t`gigCompliance.complianceTable.reqMax`;
      const headers = requirements?.contractRequirements.flatMap((item, index) => {
        if (item.requirementType !== type) return [];
        return [<RowLabelReqCell key={`header-${type}-${index}`}>{text}</RowLabelReqCell>];
      });
      if (headers?.length || type === RequirementType.max) return headers;
      return <RowLabelReqCell>{text}</RowLabelReqCell>;
    },
    [requirements?.contractRequirements, t],
  );

  const getRequirement = (requirementId: number | undefined): IContractRequirementItem | undefined => {
    return requirements?.contractRequirements.find(req => req.contractRequirementId === requirementId);
  };

  const getCells = (row: IGigComplianceRow, type: RequirementType.max | RequirementType.min) => {
    const items = type === RequirementType.min ? row.reqMin : row.reqMax;
    return items.map((item, index) => (
      <RequirementCell
        isRowPublished={row.isPublished}
        key={`${item.requirementId}-${row.weekId}`}
        value={item}
        type={type}
        weekId={row.weekId}
        index={index}
        requirement={getRequirement(item.requirementId)}
        contractSchedules={contractSchedules?.[item.contractId] ?? []}
      />
    ));
  };

  return (
    <ReqCardContainer width={tableConfig.requirements.width}>
      <LoaderWrapper
        className="h-100"
        isLoading={isLoading || isGetClientContractDetailsPending}
        loader={<RequirementsCardSkeleton />}
      >
        <ReqOverflowContainer>
          <HeaderLabelReq>
            <Title>{t`gigCompliance.complianceTable.labelRequirements`}</Title>
            <RowsLabelsReq>
              {minMaxHeaders(RequirementType.min)}
              {minMaxHeaders(RequirementType.max)}
              <RowLabelReqCell>{t`gigCompliance.complianceTable.reqBase`}</RowLabelReqCell>
              <RowLabelReqCell>{t`gigCompliance.complianceTable.reqPeak`}</RowLabelReqCell>
            </RowsLabelsReq>
          </HeaderLabelReq>
          <RowsContainer
            colorOne={tableConfig.requirements.colorOne}
            colorTwo={tableConfig.requirements.colorTwo}
            colorText={tableConfig.requirements.colorText}
          >
            {rowsToDisplay.map(row => (
              <RowReq key={row.weekSpan}>
                {getCells(row, RequirementType.min)}
                {getCells(row, RequirementType.max)}
                <RequirementCell
                  type={RequirementType.base}
                  isRowPublished={row.isPublished}
                  value={row.reqBase}
                  requirement={getRequirement(row.reqBase?.requirementId)}
                  weekId={row.weekId}
                  contractSchedules={contractSchedules?.[row?.reqBase?.contractId ?? -1] ?? []}
                />
                <RequirementCell
                  type={RequirementType.peak}
                  isRowPublished={row.isPublished}
                  value={row.reqPeak}
                  requirement={getRequirement(row.reqPeak?.requirementId)}
                  weekId={row.weekId}
                  contractSchedules={contractSchedules?.[row?.reqPeak?.contractId ?? -1] ?? []}
                />
              </RowReq>
            ))}
          </RowsContainer>
        </ReqOverflowContainer>
      </LoaderWrapper>
    </ReqCardContainer>
  );
};

export { RequirementsCard };

const HeaderLabelReq = styled(HeaderLabel)`
  margin-right: 0;
  margin-left: 0;
`;

const RowLabelReqCell = styled(RowLabelCell)`
  flex-grow: 1;
  min-width: 65px;
`;

const RowReq = styled(Row)`
  gap: 1px;

  & > div {
    margin-right: 0;
  }
`;

const RowsLabelsReq = styled(RowsLabelsContainer)`
  margin-top: 20px;
  gap: 1px;

  ${media.tablet} {
    margin-top: initial;
  }
`;

const ReqCardContainer = styled(CardContainer)`
  position: relative;

  ${media.tablet} {
    position: static;
    min-width: fit-content;
    flex-grow: 1;
  }
`;

const Title = styled.span`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translate3d(-50%, 0, 0);

  ${media.tablet} {
    position: static;
    transform: none;
  }
`;

const ReqOverflowContainer = styled.div`
  overflow: auto;

  ${media.tablet} {
    overflow: initial;
    position: static;
  }
`;
